import { ArrowUpRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const isExternal = (href) => href.startsWith("http");
const isInternalRoute = (href) =>
  href.startsWith("/") && !href.startsWith("/#");

function NewsLink({ href, children, ...rest }) {
  if (isInternalRoute(href)) {
    return (
      <Link to={href} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target={isExternal(href) ? "_blank" : undefined}
      rel={isExternal(href) ? "noopener noreferrer" : undefined}
      {...rest}
    >
      {children}
    </a>
  );
}

export default function News({ t }) {
  const items = t.news.items;

  return (
    <section
      id="news"
      data-testid="news-section"
      className="relative py-24 md:py-36 overflow-hidden border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.3em] text-[var(--primary)] mb-5">
              {t.news.kicker}
            </div>
            <h2
              data-testid="news-title"
              className="font-display font-black uppercase leading-[1] text-3xl md:text-5xl"
            >
              <span className="block text-white">{t.news.title}</span>
              <span className="block text-[var(--primary)]">
                {t.news.titleAccent}
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              data-testid="news-subtitle"
              className="text-white/70 text-base md:text-lg leading-relaxed"
            >
              {t.news.subtitle}
            </p>
          </div>
        </div>

        {/* Articles grid */}
        <div
          data-testid="news-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {items.map((post, i) => (
            <article
              key={post.id}
              data-testid={`news-card-${post.id}`}
              className="card-lift group relative flex flex-col border border-white/10 bg-[#0b101e] hover:border-white/30 transition-colors"
            >
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              {/* Image */}
              <NewsLink
                href={post.href}
                data-testid={`news-image-link-${post.id}`}
                className="relative block aspect-[16/10] overflow-hidden border-b border-white/10"
                aria-label={post.title}
              >
                <img
                  src={post.image}
                  srcSet={post.imageSrcSet}
                  sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                  alt={post.title}
                  width="640"
                  height="400"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b101e] via-[#0b101e]/30 to-transparent" />
                <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--primary)] bg-[#030508]/85 backdrop-blur-sm px-2.5 py-1.5 border border-[var(--primary)]/30">
                  {post.category}
                </span>
                <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/60">
                  // {String(i + 1).padStart(2, "0")}
                </span>
              </NewsLink>

              {/* Body */}
              <div className="flex flex-col flex-1 p-6">
                <div className="flex items-center gap-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4">
                  <time
                    dateTime={post.date}
                    data-testid={`news-date-${post.id}`}
                  >
                    {post.dateLabel}
                  </time>
                  <span className="h-1 w-1 rounded-full bg-white/30" />
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    {post.readTime}
                  </span>
                </div>

                <h3
                  data-testid={`news-card-title-${post.id}`}
                  className="font-display text-lg md:text-xl font-semibold text-white leading-snug mb-3 group-hover:text-[var(--primary)] transition-colors"
                >
                  {post.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed mb-6 flex-1">
                  {post.excerpt}
                </p>

                <NewsLink
                  href={post.href}
                  data-testid={`news-cta-${post.id}`}
                  className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase font-semibold text-[var(--primary)] hover:gap-3 transition-all"
                >
                  {t.news.readMore}
                  <ArrowUpRight className="h-4 w-4" />
                </NewsLink>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
